import PropTypes from 'prop-types';
// @mui
import { alpha, useTheme } from '@mui/material/styles';
import { Box, Button, Typography } from '@mui/material';
// components
import Page from './Page';
// universe
import Icon from './universe/Icon';
import LegalLine from './universe/LegalLine';
import BrandBanner, { SCENE_FRAME } from './universe/BrandBanner';
import {
  BRAND_LOGO_WHITE,
  RADIUS,
  UNIVERSE,
  UNIVERSE_FONT
} from '../theme/universe/brand';
import { PAGE_FONT_SX } from '../theme/universe/pageFont';
import { AUTH_SHORTER, authButtonSx } from '../layouts/auth/portals';

// ----------------------------------------------------------------------
// WHEN THE APP ITSELF CANNOT BE SHOWN (DESIGN.md §2, §29): a page that
// crashed, or a bundle a deploy has replaced while the tab was open
// ("Something new just went live"). The boundary that draws this wraps the
// whole router, so there is no rail, no header and no page behind it — the
// screen is the whole window, like the sign-in, and it is drawn the way the
// sign-in is: the Universe night with the planet's limb across its foot
// (`BrandBanner variant="scene"`) and one card laid over it, the only lit
// surface — the title, one sentence, one action at the card's 44px height.
// The illustration and the bouncing entrance the old screen had are gone
// (§11: pages appear; §15: a state is a sentence and the way out, no
// picture).
//
// There is no router out here, so nothing may be a router link: the lock-up
// is a plain anchor home, and both actions are full navigations — which is
// also what resets an app whose in-memory state has gone bad.

const nightFocusSx = {
  outline: 'none',
  boxShadow: `0 0 0 2px ${alpha('#FFFFFF', 0.9)}`
};

/** The lock-up, home — a plain anchor: there is no router out here. */
function HomeMark() {
  return (
    <Box
      component="a"
      href="/"
      aria-label="LingoYou Universe — home"
      sx={{
        position: 'relative',
        alignSelf: 'flex-start',
        display: 'inline-flex',
        borderRadius: '4px',
        transition: 'opacity .15s ease',
        '&:hover': { opacity: 0.82 },
        '&:focus-visible': nightFocusSx
      }}
    >
      <Box
        component="img"
        src={BRAND_LOGO_WHITE}
        alt="LingoYou Universe"
        sx={{ height: { xs: 26, md: 34 }, width: 'auto', display: 'block' }}
      />
    </Box>
  );
}

/** The card the message is written on: the sign-in card's sheet (§26). */
const cardSx = (theme) => ({
  position: 'relative',
  px: { xs: 3, sm: 6 },
  pt: { xs: 3.5, sm: 6 },
  pb: { xs: 3, sm: 5.5 },
  [AUTH_SHORTER]: { px: { xs: 3, sm: 4.5 }, pt: 3.5, pb: 3 },
  bgcolor: 'background.paper',
  border:
    theme.palette.mode === 'light'
      ? 'none'
      : `1px solid ${alpha('#FFFFFF', 0.1)}`,
  borderRadius: `${RADIUS.modal}px`,
  boxShadow:
    theme.palette.mode === 'light'
      ? '0 44px 88px -30px rgba(0,2,14,.74), 0 10px 28px -14px rgba(0,2,14,.5)'
      : '0 44px 88px -30px rgba(0,0,0,.92), inset 0 1px 0 rgba(255,255,255,.05)'
});

// ----------------------------------------------------------------------

ErrorFallback.propTypes = {
  isDeploy: PropTypes.bool
};

export default function ErrorFallback({ isDeploy = false }) {
  const theme = useTheme();

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    // Full navigation so a corrupted in-memory app state is fully reset.
    window.location.assign('/');
  };

  const title = isDeploy
    ? 'Something new just went live'
    : 'This page isn’t available right now';

  const description = isDeploy
    ? 'We’ve shipped an update while this page was open. Reload to get the latest version.'
    : 'We may have just pushed a new update, or something went wrong on our end. Please try again in a little while.';

  return (
    <Page title={isDeploy ? 'Update available' : 'Please try again later'}>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          minHeight: '100vh',
          overflow: 'hidden',
          backgroundColor: UNIVERSE.night
        }}
      >
        <BrandBanner variant="scene" logo={<HomeMark />} />

        <Box
          sx={{
            ...SCENE_FRAME,
            ...PAGE_FONT_SX,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            minHeight: '100vh',
            pt: { xs: 8, lg: 7 },
            pb: { xs: 4, lg: 7 },
            [AUTH_SHORTER]: { pt: { xs: 7.5, lg: 2 }, pb: { xs: 1.5, lg: 2 } }
          }}
        >
          <Box sx={{ width: 1, maxWidth: 472, mx: 'auto' }}>
            <Box role={isDeploy ? 'status' : 'alert'} sx={cardSx(theme)}>
              <Typography
                component="h1"
                sx={{
                  fontFamily: UNIVERSE_FONT,
                  fontSize: { xs: 24, md: 28 },
                  fontWeight: 700,
                  lineHeight: 1.2,
                  letterSpacing: '-0.01em',
                  color: 'text.primary',
                  textWrap: 'balance'
                }}
              >
                {title}
              </Typography>

              <Typography
                sx={{
                  mt: 1.25,
                  fontFamily: UNIVERSE_FONT,
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: 1.6,
                  color: 'text.secondary',
                  textWrap: 'balance'
                }}
              >
                {description}
              </Typography>

              {isDeploy ? (
                <Button
                  fullWidth
                  disableRipple
                  onClick={handleReload}
                  startIcon={<Icon name="Refresh2" size="sm" />}
                  sx={{ ...authButtonSx(theme), mt: 4 }}
                >
                  Reload for the latest version
                </Button>
              ) : (
                <Button
                  fullWidth
                  disableRipple
                  onClick={handleGoHome}
                  startIcon={<Icon name="ArrowLeft" size="sm" />}
                  sx={{ ...authButtonSx(theme), mt: 4 }}
                >
                  Back to home
                </Button>
              )}
            </Box>

            {/* Below 1200 the sky's copy column stands down and the legal
                line comes under the card, as on the sign-in. */}
            <LegalLine
              align="center"
              sx={{
                display: { xs: 'block', lg: 'none' },
                mt: { xs: 2.5, sm: 3 },
                mx: 'auto',
                maxWidth: 440,
                fontSize: { xs: 10.5, sm: 11 },
                letterSpacing: { xs: '0.05em', sm: '0.1em' }
              }}
            />
          </Box>
        </Box>
      </Box>
    </Page>
  );
}
